import { setImpersonationSessionProvider } from "@repo/api-client";

/**
 * The active operator-impersonation session (super-admin.md). Persisted in `sessionStorage` so a
 * reload keeps the session (and its banner) but a new tab starts clean; the id is stamped on every
 * API request via the mutator seam so impersonated calls run under the target tenant. Kept tiny +
 * framework-free so both the operator surface (which sets it) and the venue banner (which reads it)
 * share one source of truth.
 */
const KEY = "ops.impersonation.session";

export interface ActiveImpersonation {
  sessionId: string;
  venueId: string;
  operatorName?: string;
  startedAt: string;
}

const listeners = new Set<() => void>();

function read(): ActiveImpersonation | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ActiveImpersonation) : null;
  } catch {
    return null;
  }
}

export function getActiveImpersonation(): ActiveImpersonation | null {
  return read();
}

export function activeImpersonationForVenue(venueId: string): ActiveImpersonation | null {
  const current = read();
  return current && current.venueId === venueId ? current : null;
}

export function setActiveImpersonation(session: ActiveImpersonation | null): void {
  try {
    if (session) sessionStorage.setItem(KEY, JSON.stringify(session));
    else sessionStorage.removeItem(KEY);
  } catch {
    // sessionStorage unavailable (private mode / SSR) — the header seam below still no-ops safely.
  }
  for (const l of listeners) l();
}

export function subscribeImpersonation(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Wire the mutator seam ONCE at module load: every request carries the active session id (if any).
setImpersonationSessionProvider(() => read()?.sessionId);
